import * as swcHelpers from '@swc/helpers';
import { isString, isNumber, getLocalizedString } from '@grandvisionhq/utils';
import { EyeSide } from '../../../../__generated__/sharedTypes.js';
import { toBaseLineItem } from './base-line-item.js';
import { createImageUrl } from '../utils/create-image-url.js';

const isBundledContactLensesLineItem = (bundledLineItem)=>{
    return 'prescription' in bundledLineItem;
};
const isBundledSolutionLineItem = (bundledLineItem)=>{
    return 'volume' in bundledLineItem.product;
};
const localizePrescription = (prescription, intl)=>{
    const paramsLabels = {
        baseCurve: intl.getLabel('products.contactLenses.prescription.baseCurve'),
        baseCurveAbbr: intl.getLabel('products.contactLenses.prescription.baseCurve.abbr'),
        diameter: intl.getLabel('products.contactLenses.prescription.diameter'),
        diameterAbbr: intl.getLabel('products.contactLenses.prescription.diameter.abbr'),
        power: intl.getLabel('products.contactLenses.prescription.power'),
        powerAbbr: intl.getLabel('products.contactLenses.prescription.power.abbr'),
        cylinder: intl.getLabel('products.contactLenses.prescription.cylinder'),
        cylinderAbbr: intl.getLabel('products.contactLenses.prescription.cylinder.abbr'),
        axis: intl.getLabel('products.contactLenses.prescription.axis'),
        axisAbbr: intl.getLabel('products.contactLenses.prescription.axis.abbr'),
        addition: intl.getLabel('products.contactLenses.prescription.addition'),
        additionAbbr: intl.getLabel('products.contactLenses.prescription.addition.abbr'),
        dominance: intl.getLabel('products.contactLenses.prescription.dominance'),
        dominanceAbbr: intl.getLabel('products.contactLenses.prescription.dominance.abbr')
    };
    return Object.entries(prescription).reduce((m, [key, value])=>{
        if (key === '__typename') {
            return m;
        }
        /**
     * The backend should provide us with all string values for each parameter, but that's
     * not reflected in the types generated. So this is a layer of safety essentially unneeded,
     * but here because "value" is "any" here.
     */ if (!(isString(value) || isNumber(value))) {
            return m;
        }
        return [
            ...m,
            {
                key: paramsLabels[key],
                keyAbbr: paramsLabels[`${key}Abbr`],
                value: `${value}`
            }, 
        ];
    }, []);
};
const getLensParameters = (contactLensesOrderLineItem, intl)=>{
    const { prescription , eyeSide  } = contactLensesOrderLineItem || {};
    return !prescription || !eyeSide ? [] : localizePrescription(prescription, intl);
};
var toContactLensesBundleOrderLineItem = (orderLineItem, intl, options = {})=>{
    var ref, ref1;
    const base = toBaseLineItem(orderLineItem, intl, options);
    const bundledSolutionLineItem = (ref = orderLineItem.bundledLineItems) === null || ref === void 0 ? void 0 : ref.find(isBundledSolutionLineItem);
    const contactLensesProducts = (ref1 = orderLineItem.bundledLineItems) === null || ref1 === void 0 ? void 0 : ref1.filter(isBundledContactLensesLineItem);
    const leftContactLensesProduct = contactLensesProducts === null || contactLensesProducts === void 0 ? void 0 : contactLensesProducts.find((item)=>item.eyeSide === EyeSide.LEFT || item.eyeSide === EyeSide.BOTH
    );
    const rightContactLensesProduct = contactLensesProducts === null || contactLensesProducts === void 0 ? void 0 : contactLensesProducts.find((item)=>item.eyeSide === EyeSide.RIGHT || item.eyeSide === EyeSide.BOTH
    );
    const leftEyeLensParameters = getLensParameters(leftContactLensesProduct, intl);
    const rightEyeLensParameters = getLensParameters(rightContactLensesProduct, intl);
    let prescription = undefined;
    if (rightContactLensesProduct === null || rightContactLensesProduct === void 0 ? void 0 : rightContactLensesProduct.prescription) {
        const _prescription = rightContactLensesProduct.prescription, rightPrescription = swcHelpers.objectWithoutProperties(_prescription, [
            "__typename"
        ]);
        prescription = prescription || {};
        prescription.rightEye = swcHelpers.objectSpread({}, rightPrescription);
    }
    if (leftContactLensesProduct === null || leftContactLensesProduct === void 0 ? void 0 : leftContactLensesProduct.prescription) {
        const _prescription = leftContactLensesProduct.prescription, leftPrescription = swcHelpers.objectWithoutProperties(_prescription, [
            "__typename"
        ]);
        prescription = prescription || {};
        prescription.leftEye = swcHelpers.objectSpread({}, leftPrescription);
    }
    return swcHelpers.objectSpread({}, base, {
        type: 'CONTACT_LENS_BUNDLE',
        prescription,
        bundleConfiguration: {
            item: {
                name: base.title,
                lensType: getLocalizedString(orderLineItem.product.lensType, intl.locale),
                discountPercentage: undefined
            },
            leftEye: {
                name: getLocalizedString(leftContactLensesProduct === null || leftContactLensesProduct === void 0 ? void 0 : leftContactLensesProduct.product.name, intl.locale),
                parameters: leftEyeLensParameters
            },
            rightEye: {
                name: getLocalizedString(rightContactLensesProduct === null || rightContactLensesProduct === void 0 ? void 0 : rightContactLensesProduct.product.name, intl.locale),
                parameters: rightEyeLensParameters
            },
            solution: bundledSolutionLineItem ? {
                name: getLocalizedString(bundledSolutionLineItem.product.name, intl.locale)
            } : undefined
        },
        catalogPrice: orderLineItem.price.productPrice || 0,
        bundleImageUrl: createImageUrl(orderLineItem.product.mainImage, options),
        bundleImageAlt: getLocalizedString(orderLineItem.product.bundledProductName, intl.locale),
        solutionImageUrl: createImageUrl(bundledSolutionLineItem === null || bundledSolutionLineItem === void 0 ? void 0 : bundledSolutionLineItem.product.mainImage, options),
        solutionImageAlt: getLocalizedString(bundledSolutionLineItem === null || bundledSolutionLineItem === void 0 ? void 0 : bundledSolutionLineItem.product.name, intl.locale),
        /**
     * On 31/05/2022 My Account Team (@yannickbuntsma, @sergesi, etc.) had a meeting about discountDescription.
     * Took decision for Ico B. and Surajeet B. to investigate if this is used in REST API.
     * For now it is kept explicitly ndefined.
     */ discountDescription: undefined,
        // deliveryDescription is always monthly as per documentation
        // https://confluence.grandvision.global/display/GV/Order+history+attribute+overview
        deliveryDescription: intl.getLabel('common.frequency.monthly')
    });
};

export { toContactLensesBundleOrderLineItem };
//# sourceMappingURL=contact-lenses-bundle-line-item.js.map
